<template>
  <div>
    <new-user
      @newUserClosed="showNewUser = false"
      :showNewUser="showNewUser"
      :user="userService.user"
      @createUser="createUser"
    ></new-user>
    <edit-user
      @editUserClosed="showEditUser = false"
      :showEditUser="showEditUser"
      :user="userService.user"
      @updateUser="updateUser"
    />
    <widget
      :title="$tc('phrases.userManagement')"
      :button-text="$tc('phrases.newUser')"
      :button="true"
      @widgetAction="showNewUser = true"
      :subscriber="subscriber"
      :paginator="userService.paginator"
      :key="resetKey"
    >
      <div class="md-layout">
        <div class="md-layout md-gutter">
          <div
            class="md-layout-item md-size-100 md-xlarge-size-100 md-large-size-100 md-medium-size-100 md-small-size-100"
          >
            <md-table md-card style="margin-left: 0">
              <md-table-row>
                <md-table-head>
                  {{ $tc("words.id") }}
                </md-table-head>
                <md-table-head>
                  {{ $tc("words.name") }}
                </md-table-head>
                <md-table-head>
                  {{ $tc("words.email") }}
                </md-table-head>
                <md-table-head>
                  {{ $tc("words.role") }}
                </md-table-head>
                <md-table-head>
                  {{ $tc("words.phone") }}
                </md-table-head>
                <md-table-head>
                  {{ $tc("words.actions") }}
                </md-table-head>
              </md-table-row>

              <md-table-row
                @click="userDetail(user)"
                v-for="user in userService.users"
                :key="user.id"
                style="cursor: pointer"
              >
                <md-table-cell>{{ user.id }}</md-table-cell>
                <md-table-cell>{{ user.name }}</md-table-cell>
                <md-table-cell>{{ user.email }}</md-table-cell>
                <md-table-cell>
                  <md-chip
                    v-for="(role, index) in user.roles"
                    :key="index"
                    class="md-warn"
                  >
                    {{ role }}
                  </md-chip>
                </md-table-cell>
                <md-table-cell>{{ user.phone }}</md-table-cell>
                <md-table-cell>
                  <md-button
                    v-if="canDeleteUser(user)"
                    class="md-icon-button md-dense md-accent"
                    @click.stop="confirmDelete(user)"
                  >
                    <md-icon>delete</md-icon>
                  </md-button>
                </md-table-cell>
              </md-table-row>
            </md-table>
          </div>
        </div>
      </div>
    </widget>
    <md-progress-bar md-mode="indeterminate" v-if="sending" />
  </div>
</template>
<script>
import EditUser from "./EditUser.vue"
import NewUser from "./NewUser.vue"

import { notify } from "@/mixins/notify.js"
import { UserService } from "@/services/UserService.js"
import { EventBus } from "@/shared/eventbus.js"
import Widget from "@/shared/Widget.vue"

export default {
  name: "ProfileManagement",
  mixins: [notify],
  components: { Widget, NewUser, EditUser },
  data() {
    return {
      subscriber: "user-management",
      sending: false,
      showEditUser: false,
      selectedCity: 0,
      userService: new UserService(),
      userId: 0,
      showNewUser: false,
      resetKey: 1,
    }
  },
  mounted() {
    EventBus.$on("pageLoaded", this.reloadList)
    EventBus.$on("newUserCreated", () => this.resetKey++)
  },
  beforeDestroy() {
    EventBus.$off("pageLoaded")
  },
  methods: {
    reloadList(subscriber, data) {
      if (subscriber !== this.subscriber) {
        return
      }
      this.userService.updateList(data)
      EventBus.$emit(
        "widgetContentLoaded",
        this.subscriber,
        this.userService.users.length,
      )
    },
    async userDetail(user) {
      try {
        await this.userService.get(user.id)
        this.showEditUser = true
      } catch (error) {
        this.alertNotify("error", error.message)
      }
    },
    async updateUser(user) {
      this.sending = true
      if (user.cityId !== 0) {
        this.userService.user.cityId = user.cityId
      }
      // Set roles if provided
      if (user.roles) {
        this.userService.user.roles = user.roles
      }
      try {
        await this.userService.update()
        this.alertNotify("success", this.$tc("words.profile", 2))
        this.showEditUser = false
        this.resetKey++
      } catch (error) {
        this.alertNotify("error", error.message)
      }
      this.sending = false
    },
    async createUser(payload) {
      this.sending = true
      try {
        await this.userService.create(payload)
        this.alertNotify("success", this.$tc("phrases.newUser", 2))
        this.showNewUser = false
        this.resetKey++
      } catch (error) {
        this.alertNotify("error", error.message)
      }
      this.sending = false
    },
    canDeleteUser(user) {
      const currentUserId =
        this.$store.getters["auth/getAuthenticateUser"].id ||
        this.$store.getters["auth/authenticationService"].authenticateUser.id
      if (user.id === currentUserId) {
        return false
      }
      const permissions = this.$store.getters["auth/getPermissions"] || []
      const roles = user.roles || []
      if (roles.includes("owner")) {
        return permissions.includes("users.manage-owner")
      }
      if (roles.includes("admin")) {
        return permissions.includes("users.manage-admin")
      }
      return permissions.includes("users")
    },
    confirmDelete(user) {
      this.$swal({
        type: "question",
        title: this.$tc("phrases.deleteUserConfirm", 0),
        text: this.$tc("phrases.deleteUserConfirm", 2, { name: user.name }),
        showCancelButton: true,
        cancelButtonText: this.$tc("words.cancel"),
        confirmButtonText: this.$tc("words.delete"),
      }).then(async (response) => {
        if (!response.value) return
        this.sending = true
        try {
          await this.userService.delete(user.id)
          this.alertNotify("success", this.$tc("phrases.deleteUserConfirm", 1))
          this.resetKey++
        } catch (error) {
          this.alertNotify("error", error.message)
        }
        this.sending = false
      })
    },
  },
}
</script>

<style scoped lang="scss"></style>
